import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, AlertTriangle, Pill, Activity, ListChecks, ShieldAlert, Printer } from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';

const GRAVITA = {
  nessuna: 'bg-emerald-100 text-emerald-700',
  lieve: 'bg-sky-100 text-sky-700',
  moderata: 'bg-amber-100 text-amber-700',
  elevata: 'bg-red-100 text-red-700',
};

export const AnalisiParametriDialog = ({ ospiteId, ospite }) => {
  const [open, setOpen] = useState(false);
  const [giorni, setGiorni] = useState('30');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const esegui = async () => {
    setLoading(true); setRes(null);
    try {
      const r = await api.post(`/analisi/parametri/${ospiteId}`, null, { params: { giorni: Number(giorni) } });
      setRes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore analisi'); }
    setLoading(false);
  };

  const stampa = () => {
    if (!res) return;
    const a = res.analisi;
    const nome = `${ospite?.cognome || ''} ${ospite?.nome || ''}`;
    let body = `<div class="info-grid">
      <div class="info-item"><span class="info-label">Ospite</span> <span class="info-value">${nome}</span></div>
      <div class="info-item"><span class="info-label">Periodo</span> <span class="info-value">ultimi ${res.periodo_giorni} giorni</span></div>
      <div class="info-item"><span class="info-label">Confidenza</span> <span class="info-value">${a.confidenza || '-'}</span></div>
      <div class="info-item"><span class="info-label">Richiesta da</span> <span class="info-value">${res.richiesta_da}</span></div>
    </div>`;
    body += `<p class="section-title">Sintesi</p><p>${a.sintesi || ''}</p>`;
    if (res.riepilogo_dati?.length) {
      body += `<p class="section-title">Dati rilevati</p><table><thead><tr><th>Parametro</th><th>Rilev.</th><th>Media</th><th>Min-Max</th><th>Ultimo</th><th>Fuori range</th><th>Trend</th></tr></thead><tbody>`;
      res.riepilogo_dati.forEach(p => {
        body += `<tr><td>${p.parametro}</td><td>${p.rilevazioni}</td><td>${p.media ?? '-'}</td><td>${p.min ?? '-'} - ${p.max ?? '-'}</td><td>${p.ultimo ?? (p.valori_testuali?.slice(-1)[0] || '-')}</td><td>${p.fuori_range}</td><td>${p.trend || '-'}</td></tr>`;
      });
      body += `</tbody></table>`;
    }
    if (a.per_parametro?.length) {
      body += `<p class="section-title">Valutazione per parametro</p><table><thead><tr><th>Parametro</th><th>Andamento</th><th>Gravita</th><th>Valutazione</th></tr></thead><tbody>`;
      a.per_parametro.forEach(p => { body += `<tr><td>${p.parametro}</td><td>${p.andamento || ''}</td><td>${p.gravita || ''}</td><td>${p.valutazione || ''}</td></tr>`; });
      body += `</tbody></table>`;
    }
    if (a.correlazioni_terapia?.length) {
      body += `<p class="section-title">Correlazioni con la terapia in corso</p><ul>${a.correlazioni_terapia.map(c => `<li><b>${c.farmaco}</b>: ${c.osservazione}</li>`).join('')}</ul>`;
    }
    if (a.criticita?.length) body += `<p class="section-title">Criticita</p><ul>${a.criticita.map(c => `<li>${c}</li>`).join('')}</ul>`;
    if (a.rischi_correlati?.length) body += `<p class="section-title">Rischi correlati</p><ul>${a.rischi_correlati.map(c => `<li>${c}</li>`).join('')}</ul>`;
    if (a.raccomandazioni_monitoraggio?.length) body += `<p class="section-title">Raccomandazioni di monitoraggio</p><ul>${a.raccomandazioni_monitoraggio.map(c => `<li>${c}</li>`).join('')}</ul>`;
    body += `<p style="font-size:9px;color:#64748b;margin-top:8px">Analisi assistita da intelligenza artificiale (${res.modello}) su dati aggregati. Supporto alla valutazione clinica, non sostituisce il giudizio del medico. Limiti: ${a.limiti || '-'}</p>`;
    body += `<div class="signature-row"><div class="signature-block">Il Medico</div><div class="signature-block">Data</div></div>`;
    openPrintWindow('Analisi Parametri Vitali', body, { subtitle: `${nome} - ultimi ${res.periodo_giorni} giorni` });
  };

  const a = res?.analisi;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="h-8 text-xs border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="analisi-ia-btn">
        <Sparkles className="w-3.5 h-3.5 mr-1" />Analisi IA
      </Button>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" />Analisi IA dei Parametri Vitali</DialogTitle></DialogHeader>

        <div className="flex items-center gap-2">
          <Select value={giorni} onValueChange={setGiorni}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="analisi-giorni-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              <SelectItem value="180">Ultimi 6 mesi (180 giorni)</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={esegui} disabled={loading} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="analisi-esegui-btn">
            {loading ? 'Analisi in corso...' : 'Analizza'}
          </Button>
          {res && <Button size="sm" variant="outline" onClick={stampa} data-testid="analisi-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Stampa</Button>}
        </div>

        {loading && <p className="text-xs text-slate-400 py-8 text-center">Elaborazione dei trend, della terapia in corso e delle diagnosi...</p>}

        {a && (
          <div className="space-y-3" data-testid="analisi-risultato">
            <div className="p-3 rounded-xl bg-violet-50 border border-violet-200 text-xs">
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-slate-800">Sintesi del periodo</p>
                <Badge variant="outline" className="text-[10px]">confidenza {a.confidenza || 'n/d'}</Badge>
              </div>
              <p className="text-slate-700">{a.sintesi}</p>
            </div>

            {res.riepilogo_dati?.length > 0 && (
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr><th className="text-left px-2 py-1.5">Parametro</th><th className="px-2">Rilev.</th><th className="px-2">Media</th><th className="px-2">Min-Max</th><th className="px-2">Fuori range</th><th className="px-2">Trend</th></tr>
                  </thead>
                  <tbody>
                    {res.riepilogo_dati.map((p, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`analisi-dato-${i}`}>
                        <td className="px-2 py-1.5 font-medium text-slate-700">{p.parametro}</td>
                        <td className="px-2 text-center">{p.rilevazioni}</td>
                        <td className="px-2 text-center">{p.media ?? '-'}</td>
                        <td className="px-2 text-center">{p.min ?? '-'} - {p.max ?? '-'}</td>
                        <td className={`px-2 text-center ${p.fuori_range > 0 ? 'text-red-600 font-semibold' : ''}`}>{p.fuori_range}</td>
                        <td className="px-2 text-center">{p.trend || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {a.per_parametro?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-blue-500" />Valutazione per parametro</p>
                {a.per_parametro.map((p, i) => (
                  <div key={i} className="p-2 rounded-lg border border-slate-200 bg-white text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-800">{p.parametro}</span>
                      <div className="flex gap-1.5">
                        <Badge variant="outline" className="text-[9px]">{p.andamento}</Badge>
                        <Badge className={`text-[9px] ${GRAVITA[p.gravita] || 'bg-slate-100 text-slate-600'}`}>{p.gravita}</Badge>
                      </div>
                    </div>
                    <p className="text-slate-600 mt-0.5">{p.valutazione}</p>
                  </div>
                ))}
              </div>
            )}

            {a.correlazioni_terapia?.length > 0 && (
              <div className="p-3 rounded-xl border border-slate-200 bg-white text-xs">
                <p className="font-semibold text-slate-700 flex items-center gap-1.5 mb-1"><Pill className="w-3.5 h-3.5 text-rose-500" />Correlazioni con la terapia in corso</p>
                <ul className="space-y-1">{a.correlazioni_terapia.map((c, i) => (
                  <li key={i} className="text-slate-600"><span className="font-medium text-slate-800">{c.farmaco}</span>: {c.osservazione}</li>
                ))}</ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {a.criticita?.length > 0 && (
                <div className="p-3 rounded-xl border border-red-200 bg-red-50 text-xs">
                  <p className="font-semibold text-red-800 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Criticita</p>
                  <ul className="list-disc list-inside space-y-0.5 text-red-700">{a.criticita.map((c, i) => <li key={i}>{c}</li>)}</ul>
                </div>
              )}
              {a.rischi_correlati?.length > 0 && (
                <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-xs">
                  <p className="font-semibold text-amber-800 flex items-center gap-1.5 mb-1"><ShieldAlert className="w-3.5 h-3.5" />Rischi correlati</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700">{a.rischi_correlati.map((c, i) => <li key={i}>{c}</li>)}</ul>
                </div>
              )}
            </div>

            {a.raccomandazioni_monitoraggio?.length > 0 && (
              <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50 text-xs">
                <p className="font-semibold text-emerald-800 flex items-center gap-1.5 mb-1"><ListChecks className="w-3.5 h-3.5" />Raccomandazioni di monitoraggio</p>
                <ul className="list-disc list-inside space-y-0.5 text-emerald-700">{a.raccomandazioni_monitoraggio.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </div>
            )}

            <p className="text-[10px] text-slate-500 italic">
              Analisi assistita da AI ({res.modello}) su dati aggregati: supporto alla valutazione, non sostituisce il giudizio del medico. {a.limiti}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnalisiParametriDialog;
